<!--
  PaymentStatusLine.vue
  콜봇 월 정산 · 읽기 전용 '수납 상태' 한 줄 — 정산 탭 확정 영역(tabs/StatementPanel.vue)과 재수정 창(ReopenModal.vue)이
  같이 쓴다.

  요청: ~/md/front/admin-콜봇-월정산-카드결제-프론트요청.md 결론 3(읽기 전용 '수납 상태' 한 줄), 요청 계약 3·4
  계약: ~/md/plan/콜봇-월정산-카드결제-조사/v3/contract/contract-pack.md 9.1(`payment`·`payment_error`)·8.4(이름표)
  - 모양은 service/settlementPayment.js paymentStatusLine 이 만든다 — 이 컴포넌트는 그리기만 한다. 숫자는 서버 값
    그대로다(목표·받은 금액·남은 금액·기한을 다시 계산하지 않고, 카드·계좌 몫을 더하지 않는다 — 결론 3-5).
  - `payment`·`payment_error` 가 다 null 이면(칸이 없는 옛 call-api 포함) 아무것도 그리지 않는다(결론 3-1). health 로
    가르지 않는다. `payment_error` 면 그 자리에 "수납 상태를 읽지 못했습니다"만 보인다.
  - 그 밖: "수납 상태 · 계좌이체 · 기본료 제외 · 입금 대기 · 목표 n원 · 받은 금액 n원(카드 n원 · 계좌 n원) · 남은 금액
    n원 · 기한 M월 D일"(결론 3-3 의 차례, 요청 계약 4 의 표기). 툴팁이 있는 칸(기본료 제외, 목표의 공급가액·부가세,
    막힘·실패 사유와 모의 실행)은 a-tooltip 으로, '기한 지남'은 빨간 글씨로 보인다. 보류·멈춤 사유는 한 줄 아래에
    ' · ' 로 이어 한 줄로(요청 계약 4), 맨 아래에 "수납·환불은 admin-reseller '콜봇 사용료'에서 합니다."(결론 3-4).
  - 동작 버튼은 없다(결정 3 — 수납·환불은 대표가 admin-reseller 에서 한다).
  - highlightReceived(재수정 창): 받은 금액(`pw.net`)이 0 보다 크면 경고 색으로 보인다(결론 3-6).

  - props: detail(정산 상세 응답, 없으면 null) · highlightReceived
-->
<template>
  <div v-if="line.visible" class="pay-line" :class="{ 'is-warn': line.tone === 'warn' }">
    <p v-if="line.error" class="pay-line__error">{{ line.text }}</p>
    <template v-else>
      <p class="pay-line__main">
        <strong class="pay-line__title">{{ line.title }}</strong>
        <template v-for="part in line.parts" :key="part.key">
          <span class="pay-line__sep">{{ SEPARATOR }}</span>
          <a-tooltip v-if="part.tooltip" :title="part.tooltip">
            <span class="pay-line__part has-tip" :class="{ 'is-danger': part.tone === 'danger' }">{{ part.text }}</span>
          </a-tooltip>
          <span v-else class="pay-line__part" :class="{ 'is-danger': part.tone === 'danger' }">{{ part.text }}</span>
        </template>
      </p>
      <p v-if="line.reasonText" class="pay-line__reasons">{{ line.reasonText }}</p>
      <p class="pay-line__footer">{{ line.footer }}</p>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';

import { paymentStatusLine } from '../service/settlementPayment';

/** 칸 사이 — paymentStatusLine 의 lineText 와 같은 ' · ' */
const SEPARATOR = ' · ';

const props = defineProps({
  /** 정산 상세 응답(`payment`·`payment_error`·`statement.rev`) — 없으면 그리지 않는다 */
  detail: { type: Object, default: null },
  /** 재수정 창 — 받은 금액(`pw.net`)이 0 보다 크면 경고 색(AC 결제 요청 결론 3-6) */
  highlightReceived: { type: Boolean, default: false },
});

const line = computed(() => paymentStatusLine(props.detail, { highlightReceived: props.highlightReceived }));
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

.pay-line {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid $st-border;
  border-radius: 8px;
  background: #fff;
  font-size: 12.5px;
  line-height: 1.6;
  color: $st-text;
  word-break: keep-all;

  // 재수정 창에서 이미 받은 돈이 있는 달
  &.is-warn {
    border-color: $st-warning-border;
    background: $st-warning-bg;
  }

  &__main,
  &__reasons,
  &__footer,
  &__error {
    margin: 0;
  }

  &__title {
    font-weight: 600;
  }

  &__sep {
    color: $st-muted;
  }

  // 칸은 통째로 줄을 넘긴다(한 줄보다 긴 칸만 안에서 줄바꿈)
  &__part {
    display: inline-block;
    max-width: 100%;

    &.has-tip {
      border-bottom: 1px dotted $st-muted;
      cursor: help;
    }

    // '기한 지남'
    &.is-danger {
      font-weight: 600;
      color: $st-danger;
    }
  }

  // 보류·멈춤 사유 — 결제를 막고 있는 것
  &__reasons {
    color: $st-warning;
  }

  &__footer {
    font-size: 12px;
    color: $st-muted;
  }

  &__error {
    color: $st-text-secondary;
  }
}
</style>
